import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Spin, Table, Typography, Tag, Row, Col, Space, DatePicker } from 'antd';
import Filters from '../../../components/commonComponents/filter';
import SearchBar from '../../../components/commonComponents/searchBar';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { getSingleProduct, getProductPaymentTransactions } from '../api/api';
import { useSelector } from 'react-redux';
import NotificationContext from '../../../providers/NotificationProvider';
import { showErrorToast } from '../../../components/commonComponents/gxAntdToast/toastUtils';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const ProductDetails = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedStatusIds, setSelectedStatusIds] = useState([]);
  // Helper to get default date range (last 7 days)
  const getDefaultDateRange = () => [
    dayjs().subtract(6, 'days').startOf('day'),
    dayjs().endOf('day')
  ];

  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const { currentBrand } = useSelector((state) => state.app);
  const { RangePicker } = DatePicker;
  const notification = React.useContext(NotificationContext);
  const searchInputRef = useRef(null);

  // Status options for the filter
  const statusOptions = [
    {
      id: 1,
      name: "STATUS",
      subFilter: [
        {
          id: 1,
          label: "Draft",
          value: 0,
          status: "Draft",
        },
        {
          id: 2,
          label: "Pending",
          value: 5,
          status: "Pending",
        },
        {
          id: 3,
          label: "Completed",
          value: 10,
          status: "Completed",
        },
        {
          id: 4,
          label: "Failed",
          value: -1,
          status: "Failed",
        },
        {
          id: 5,
          label: "Bounced",
          value: -10,
          status: "Bounced",
        },
      ],
    }
  ];

  const handleSearch = useCallback((value) => {
    setSearchText(value);
  }, []);

  // Handle search and filter changes
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchTransactions({
        search: searchText.trim() || undefined,
        status: selectedStatusIds.length > 0 ? selectedStatusIds : undefined,
        pagination: { ...pagination, current: 1 }
      });
    }, 400);

    return () => clearTimeout(timer);
  }, [searchText, JSON.stringify(selectedStatusIds)]);

  const fetchProductDetails = async () => {
    try {
      setLoading(true);
      const productResponse = await getSingleProduct(productId, currentBrand?.brand_id);
      
      if (productResponse?.data?.product) {
        setProduct(productResponse.data.product);
      }
    } catch (error) {
      console.error('Error fetching product details:', error);
      showErrorToast(notification, 'Failed to load product details');
      navigate('/user/products/list');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = useCallback(async (params = {}) => {
    if (!currentBrand?.brand_id) return;

    try {
      setTransactionsLoading(true);
      const { current, pageSize, status, search, pagination: paginationParam, ...filters } = params;
      
      // Convert array of statuses to comma-separated string if it's an array
      let statusParam = status;
      if (Array.isArray(statusParam) && statusParam.length > 0) {
        statusParam = statusParam.join(',');
      } else if (!statusParam) {
        statusParam = undefined;
      }

      // Prepare query parameters - explicitly list only the parameters we want to send
      const queryParams = {
        ...(search && { search }),
        ...(statusParam && { status: statusParam }),
        ...(filters.start_date && { start_date: filters.start_date }),
        ...(filters.end_date && { end_date: filters.end_date }),
        page: current || pagination.current,
        limit: pageSize || pagination.pageSize
      };

      // Clean up any undefined values
      Object.keys(queryParams).forEach(key => {
        if (queryParams[key] === undefined) {
          delete queryParams[key];
        }
      });

      const response = await getProductPaymentTransactions(
        productId,
        currentBrand.brand_id,
        queryParams
      );

      if (response?.data) {
        setTransactions(response.data.payment_transactions || []);
        setTotalCount(response.data.product_payment_amount || 0.00);
        setPagination({
          current: current || pagination.current,
          pageSize: pageSize || pagination.pageSize,
          total: response.data.payment_transactions_count || 0,
        });
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      showErrorToast(notification, 'Failed to load transactions');
    } finally {
      setTransactionsLoading(false);
    }
  }, [pagination.current, pagination.pageSize, debouncedSearch, currentBrand?.brand_id, notification]);

  const handleFilterChange = (data, item) => {
    if (item) {
      // Toggle the selected status
      setSelectedStatusIds(prev => {
        const newStatusIds = prev.includes(item.value)
          ? prev.filter(id => id !== item.value) // Remove if exists
          : [...prev, item.value]; // Add if not exists

        // Trigger fetch with new filters
        fetchTransactions({
          search: searchText.trim() || undefined,
          status: newStatusIds.length > 0 ? newStatusIds : undefined,
          pagination: { ...pagination, current: 1 }
        });

        return newStatusIds;
      });
    }
  };

  const handleDateRangeChange = (dates) => {
    if (!dates || !dates[0] || !dates[1]) {
      // If dates are cleared, reset to default range (last 7 days)
      const defaultRange = getDefaultDateRange();
      setDateRange(defaultRange);

      fetchTransactions({
        search: searchText.trim() || undefined,
        status: selectedStatusIds.length > 0 ? selectedStatusIds : undefined,
        start_date: defaultRange[0].format('YYYY-MM-DD'),
        end_date: defaultRange[1].format('YYYY-MM-DD'),
        pagination: { ...pagination, current: 1 }
      });
      return;
    }

    // Ensure we have valid dates before proceeding
    const [start, end] = [dates[0].startOf('day'), dates[1].endOf('day')];

    // Update the date range state
    setDateRange([start, end]);

    // Fetch transactions with the new date range
    fetchTransactions({
      search: searchText.trim() || undefined,
      status: selectedStatusIds.length > 0 ? selectedStatusIds : undefined,
      start_date: start.format('YYYY-MM-DD'),
      end_date: end.format('YYYY-MM-DD'),
      pagination: { ...pagination, current: 1 }
    });
  };

  const clearAllFilters = () => {
    setSelectedStatusIds([]);
    setSearchText('');
    const defaultRange = getDefaultDateRange();
    setDateRange(defaultRange);

    // Reset to first page with default date range
    fetchTransactions({
      search: '',
      status: undefined,
      start_date: defaultRange[0].format('YYYY-MM-DD'),
      end_date: defaultRange[1].format('YYYY-MM-DD'),
      pagination: { ...pagination, current: 1 }
    });
  };

  // Initialize component data
  useEffect(() => {
    if (currentBrand?.brand_id) {
      // Fetch both product details and transactions in parallel
      const initData = async () => {
        await fetchProductDetails();
        // Apply default date range filter
        const defaultRange = getDefaultDateRange();
        await fetchTransactions({
          start_date: defaultRange[0].format('YYYY-MM-DD'),
          end_date: defaultRange[1].format('YYYY-MM-DD'),
          current: 1,
          pageSize: 10
        });
      };

      initData();
    }
  }, [productId, currentBrand?.brand_id]);

  const columns = [
    {
      title: 'ORDER',
      dataIndex: 'reference_id',
      key: 'reference_id',
      align: 'left',
      render: (text) => <Text strong># {text}</Text>,
    },
    {
      title: 'Medium',
      dataIndex: 'medium',
      key: 'medium',
      align: 'left',
      render: (medium) => (
        <div style={{ textTransform: 'capitalize' }}>
          {medium || '-'}
        </div>
      ),
      onFilter: (value, record) => record.medium === value,
    },
    {
      title: 'Goods Type',
      dataIndex: 'goods_type',
      key: 'goods_type',
      align: 'left',
      render: (goods_type) => (
        <div style={{ textTransform: 'capitalize' }}>
          {goods_type || '-'}
        </div>
      ),
      onFilter: (value, record) => record.medium === value,
    },
    {
      title: 'Quantity',
      dataIndex: 'pti_quantity',
      key: 'pti_quantity',
      align: 'left',
    },
    {
      title: 'Amount',
      dataIndex: 'pti_amount',
      key: 'amount',
      align: 'left',
      render:  (price) => (
        <div className="font-medium text-left">
          ₹{(parseFloat(price || 0) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
      ),
      sorter: (a, b) => a.pti_amount - b.pti_amount,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'left',
      render: (status) => {
        const statusMap = {
          0: { text: 'Draft', color: 'default', bgcolor: 'default' },
          5: { text: 'Pending', color: 'orange', bgcolor: 'orange' },
          10: { text: 'Completed', color: 'var(--primary)', bgcolor: 'var(--primaryBackground)' },
          '-1': { text: 'Failed', color: 'var(--fontRed)', bgcolor: 'var(--lightRed)' },
          '-10': { text: 'Bounced', color: 'var(--fontRed)', bgcolor: 'var(--lightRed)' }
        };
        
        const statusInfo = statusMap[status] || { 
          text: status,
          color: 'default',
          bgcolor: 'default'
        };
        
        return (
          <Tag 
            color={statusInfo.color}
            className="rounded-md text-md"
          >
            {statusInfo.text}
          </Tag>
        );
      },
    },
    {
      title: 'Payment Config',
      dataIndex: 'configuration_name',
      key: 'configuration_name',
      align: 'left',
      render: (configuration_name) => (
        <div style={{ textTransform: 'capitalize' }}>
          {configuration_name || '-'}
        </div>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'date',
      align: 'left',
      render: (date) => dayjs(date).format('MMM D, YYYY HH:mm'),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    },
  ];
  
  const handleTableChange = (pagination, filters, sorter) => {
    fetchTransactions({
      current: pagination.current,
      pageSize: pagination.pageSize,
      ...filters,
      ...(sorter.field && {
        sortField: sorter.field,
        sortOrder: sorter.order,
      }),
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="p-6">
        <Button 
          type="text" 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate(-1)}
          className="mb-4 text-base font-medium"
          style={{ padding: '4px 0' }}
        >
          Back to Products
        </Button>
        <Card>
          <div className="text-center py-8">
            <Text type="secondary">Product not found</Text>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6">
      <Button
        type="default"
        size="large"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(-1)}
        className="mb-6 flex items-center gap-2"
      >
        <span className="text-gray-800 font-medium">Back to Products</span>
      </Button>

      <Card className="mb-6">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <Title level={3} className="mb-1">{product.name}</Title>
              {product.sku && <Text type="secondary" className="block">SKU: {product.sku}</Text>}
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <Text strong className="block text-md">
                  TOTAL : ₹{parseFloat(totalCount / 100  || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </Text>
              </div>
            </div>
          </div>

          {product.description && (
            <div className="mb-4">
              <Text strong className="block mb-1">Description</Text>
              <Text>{product.description}</Text>
            </div>
          )}
        </div>
      </Card>

      <div className="bg-white rounded-lg shadow-sm mb-6 p-6">
        {/* Header with Search and Filters */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="w-full md:w-1/3">
            <SearchBar
              ref={searchInputRef}
              searchKey={searchText}
              setSearchKey={setSearchText}
              onChange={handleSearch}
              expandable={false}
              placeholder="Search orders..."
            />
          </div>
          <div className="flex items-center gap-4">
            <RangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              style={{ width: 250 }}
              placeholder={['Start Date', 'End Date']}
              format="DD/MM/YYYY"
              className="border-gray-300 rounded-md"
              disabledDate={(current) => {
                return current && current > dayjs().endOf('day');
              }}
            />
            <Filters
              options={statusOptions}
              selectedOptions={selectedStatusIds}
              loading={transactionsLoading}
              onChange={handleFilterChange}
              onClearAll={clearAllFilters}
              placeholder="Status Filter"
            />
          </div>
        </div>
        {/* Table */}
        <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
          <Table
            columns={columns}
            dataSource={transactions}
            rowKey="id"
            loading={transactionsLoading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: false,
              showTotal: (total) => `Total ${total} orders`,
              className: 'px-6 py-4',
              style: {
                margin: 0,
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.5rem',
                justifyContent: 'flex-end'
              },
              pageSizeOptions: ['10', '20', '50', '100']
            }}
            onChange={handleTableChange}
            scroll={{ x: 'max-content' }}
            className="[&_.ant-table-thead>tr>th]:bg-gray-50 [&_.ant-table-thead>tr>th]:text-gray-600 [&_.ant-table-thead>tr>th]:font-medium [&_.ant-table-tbody>tr>td]:py-4"
            locale={{
              emptyText: 'No orders found for this product'
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
